import React, { useEffect, useState } from 'react'
import { Button, Modal, Paper, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import speeex_logo_modal from '../../assets/images/logon.svg'
import pubsub from 'pubsub-js'
import { submitOnboardingSurvey } from './onboardingSurveyApi'
import {
  ONBOARDING_SURVEY_30_FORM_URL,
  ONBOARDING_SURVEY_60_FORM_URL,
} from '../../utils/consts'

const ONBOARDING_SURVEY_FORM_URL_BY_TYPE = {
  onboarding_30: ONBOARDING_SURVEY_30_FORM_URL,
  onboarding_60: ONBOARDING_SURVEY_60_FORM_URL,
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    boxShadow: theme.shadows[8],
    maxWidth: 720,
    maxHeight: '90vh',
    overflow: 'auto',
    position: 'relative',
  },
  header: {
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #dee2e6',
    padding: theme.spacing(2, 3),
    textAlign: 'center',
  },
  logo: {
    width: '100%',
    height: 64,
    marginBottom: theme.spacing(1.5),
    objectFit: 'contain',
  },
  title: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: '#2c3e50',
  },
  content: {
    padding: theme.spacing(2, 3),
  },
  linkButtonWrap: {
    marginTop: theme.spacing(2),
  },
  submitHint: {
    fontSize: '0.8rem',
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(2, 3),
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #dee2e6',
  },
}))

const OnboardingSurveyModal = ({
  open,
  pendingPayload,
  onCloseSuccess,
  onDismiss,
  dismissAllowed = true,
}) => {
  const classes = useStyles()
  const [submitting, setSubmitting] = useState(false)
  const [formLinkOpened, setFormLinkOpened] = useState(false)

  useEffect(() => {
    if (open && pendingPayload?.surveyType != null) {
      setFormLinkOpened(false)
    }
  }, [open, pendingPayload?.surveyType])

  if (!open || !pendingPayload) {
    return null
  }

  const { title, description, formUrl, surveyType } = pendingPayload
  const resolvedFormUrl =
    (ONBOARDING_SURVEY_FORM_URL_BY_TYPE[surveyType] || '').trim() || formUrl
  if (!title || !resolvedFormUrl) {
    return null
  }

  const handleOpenForm = () => {
    setFormLinkOpened(true)
    window.open(resolvedFormUrl, '_blank', 'noopener,noreferrer')
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (submitting || !formLinkOpened) return

    setSubmitting(true)
    submitOnboardingSurvey({ surveyType })
      .then((res) => {
        if (res.data?.success !== false) {
          pubsub.publish('showNotification', {
            message: 'Faleminderit! Konfirmimi u ruajt.',
            type: 'success',
          })
          onCloseSuccess?.()
        } else {
          pubsub.publish('showNotification', {
            message: res.data?.message || 'Dështoi ruajtja.',
            type: 'error',
          })
        }
      })
      .catch((err) => {
        const status = err.response?.status
        if (status === 409) {
          pubsub.publish('showNotification', {
            message: 'Këtë hap e keni konfirmuar tashmë.',
            type: 'info',
          })
          onCloseSuccess?.()
          return
        }
        if (status === 400) {
          pubsub.publish('showNotification', {
            message: err.response?.data?.message || 'Të dhëna të pavlefshme.',
            type: 'error',
          })
          return
        }
        console.warn('OnboardingSurveyModal submit error', err)
        pubsub.publish('showNotification', {
          message: 'Ndodhi një gabim. Ju lutemi provoni sërish më vonë.',
          type: 'error',
        })
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleRequestClose = (event, reason) => {
    if (!dismissAllowed) {
      return
    }
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      onDismiss?.()
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleRequestClose}
      className={classes.modal}
      aria-labelledby="onboarding-survey-title"
    >
      <Paper className={classes.paper}>
        <div className={classes.header}>
          <img src={speeex_logo_modal} alt="SPEEEX" className={classes.logo} />
          <Typography id="onboarding-survey-title" className={classes.title}>
            {title}
          </Typography>
          {description && (
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
              {description}
            </Typography>
          )}
        </div>
        <form onSubmit={handleSubmit}>
          <div className={classes.content}>
            <Typography variant="body2" color="textSecondary">
              Hapni formularin në faqen e jashtme, plotësojeni, pastaj klikoni «Dërgo» këtu për të vazhduar në sistem.
            </Typography>
            <div className={classes.linkButtonWrap}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                fullWidth
                onClick={handleOpenForm}
              >
                Hap formularin
              </Button>
              {!formLinkOpened && (
                <Typography className={classes.submitHint}>
                  Duhet të klikoni «Hap formularin» para se të dërgoni konfirmimin.
                </Typography>
              )}
            </div>
          </div>
          <div
            className={classes.actions}
            style={!dismissAllowed ? { justifyContent: 'flex-end' } : undefined}
          >
            {dismissAllowed && (
              <Button
                type="button"
                variant="outlined"
                color="default"
                disabled={submitting}
                onClick={() => onDismiss?.()}
              >
                Më vonë
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={submitting || !formLinkOpened}
            >
              {submitting ? 'Duke dërguar…' : 'Dërgo'}
            </Button>
          </div>
        </form>
      </Paper>
    </Modal>
  )
}

export default OnboardingSurveyModal
